import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import StickyPageHeader from '../components/common/StickyPageHeader';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight, Wallet, AlertTriangle, Calendar, CalendarDays, CalendarRange, SlidersHorizontal, X, TrendingUp, TrendingDown, Settings2, Book } from 'lucide-react';
import { useStatsQuery, useAccountsQuery, useCategoriesQuery } from '../hooks/useQueries';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { useToast } from '../context/ToastContext';
import DateTimeInput from '../components/common/DateTimeInput';
import MobileSelect from '../components/common/MobileSelect';
import { getIconComponent } from '../components/common/IconPicker';
import useModalBack from '../hooks/useModalBack';
import { StatsPageSkeleton } from '../components/common/Skeleton';

const FALLBACK_COLORS = [
  '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
];

const PERIOD_OPTIONS = [
  { value: 'thisMonth', label: 'This Month', icon: CalendarDays, iconColor: '#8b5cf6' },
  { value: 'lastMonth', label: 'Last Month', icon: Calendar, iconColor: '#3b82f6' },
  { value: 'annually', label: 'This Year', icon: CalendarRange, iconColor: '#22c55e' },
  { value: 'custom', label: 'Custom', icon: Settings2, iconColor: '#6b7280' }
];

function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export default function StatsPage() {
  const { hasEncryptionKey } = useAuth();
  const { formatAmount, formatDate, formatTime, config } = useConfig();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const type = searchParams.get('type') || 'expense';
  const period = searchParams.get('period') || 'thisMonth';
  const bankName = searchParams.get('bank') || '';
  const startDate = searchParams.get('startDate') || '';
  const endDate = searchParams.get('endDate') || '';

  const [hiddenCategories, setHiddenCategories] = useState(new Set());
  const [showFilterModal, setShowFilterModal] = useState(false);
  const closeFilterModal = useCallback(() => setShowFilterModal(false), []);
  const openFilterModal = useCallback(() => setShowFilterModal(true), []);
  const { closeSilent: closeSilentFilter } = useModalBack(showFilterModal, closeFilterModal, 'StatsFilter');

  const [tempPeriod, setTempPeriod] = useState(period);
  const [tempBankName, setTempBankName] = useState(bankName);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [autoOpenEndDate, setAutoOpenEndDate] = useState(false);

  // Desktop date local state — source of truth for date values
  // Don't sync back from URL params (causes race condition where value goes empty)
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);

  useEffect(() => {
    if (showFilterModal) {
      setTempPeriod(period);
      setTempBankName(bankName);
      setTempStartDate(startDate);
      setTempEndDate(endDate);
    }
  }, [showFilterModal, period, bankName, startDate, endDate]);

  const { data: accounts = [] } = useAccountsQuery({}, { enabled: hasEncryptionKey });
  const { data: categories = [] } = useCategoriesQuery({}, { enabled: hasEncryptionKey });
  const accountOptions = useMemo(() => {
    return [
      { value: '', label: 'All Accounts', icon: Wallet, iconColor: '#6b7280' },
      ...accounts.filter(a => a.isActive !== false).map(acc => ({
        value: acc.name,
        label: acc.name,
        icon: getIconComponent(acc.icon),
        iconColor: acc.color
      }))
    ];
  }, [accounts]);

  // Category icon map
  const categoryIconMap = useMemo(() => {
    const map = {};
    categories.forEach(cat => {
      map[`${cat.type}-${cat.name}`] = { icon: getIconComponent(cat.icon), color: cat.color };
    });
    return map;
  }, [categories]);

  const queryParams = useMemo(() => {
    const params = { type, period };
    if (bankName) params.account = bankName;
    if (period === 'custom') {
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
    }
    return params;
  }, [type, period, bankName, startDate, endDate]);

  const { data: statsData, isLoading } = useStatsQuery(queryParams, {
    enabled: hasEncryptionKey
  });

  const updateFilter = (key, value, extraUpdates = {}) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    // Apply extra updates in same batch (e.g., reset endDate when startDate changes)
    Object.entries(extraUpdates).forEach(([k, v]) => {
      if (v) newParams.set(k, v);
      else newParams.delete(k);
    });
    setSearchParams(newParams);
  };

  const toggleCategory = (category) => {
    setHiddenCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) newSet.delete(category);
      else newSet.add(category);
      return newSet;
    });
  };

  // Navigate to category transactions page
  const navigateToCategory = useCallback((item) => {
    const now = new Date();
    let catStartDate, catEndDate;
    if (period === 'custom' && startDate && endDate) {
      catStartDate = startDate;
      catEndDate = endDate;
    } else if (period === 'lastMonth') {
      catStartDate = formatLocalDate(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      catEndDate = formatLocalDate(new Date(now.getFullYear(), now.getMonth(), 0));
    } else if (period === 'thisMonth') {
      catStartDate = formatLocalDate(new Date(now.getFullYear(), now.getMonth(), 1));
      catEndDate = formatLocalDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    } else if (period === 'annually') {
      catStartDate = formatLocalDate(new Date(now.getFullYear(), 0, 1)); // Jan 1
      catEndDate = formatLocalDate(new Date(now.getFullYear(), 11, 31)); // Dec 31
    }
    navigate('/category-transactions', {
      state: {
        category: {
          name: item.name,
          type: item.itemType,
          color: item.color,
          total: item.value,
          count: item.count,
          icon: categoryIconMap[`${item.itemType}-${item.name}`]?.icon ? undefined : undefined,
          budget: item.budget,
          percentage: item.percentage
        },
        startDate: catStartDate,
        endDate: catEndDate
      }
    });
  }, [navigate, period, startDate, endDate, categoryIconMap]);

  // Chart data
  const chartData = useMemo(() => {
    if (!statsData?.categoryBreakdown) return [];
    const { income, expense } = statsData.categoryBreakdown;
    let data = [];

    if (type === 'income' || type === 'all') {
      Object.entries(income || {}).forEach(([category, values], index) => {
        data.push({
          category, total: values.total, count: values.count, percentage: values.percentage,
          type: 'income', color: values.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length]
        });
      });
    }
    if (type === 'expense' || type === 'all') {
      Object.entries(expense || {}).forEach(([category, values], index) => {
        data.push({
          category, total: values.total, count: values.count, percentage: values.percentage,
          type: 'expense', color: values.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length],
          budget: values.budget, budgetPeriod: values.budgetPeriod
        });
      });
    }
    return data.sort((a, b) => b.total - a.total);
  }, [statsData, type]);

  const allChartData = chartData.map((item, index) => ({
    name: item.category,
    value: item.total,
    count: item.count,
    percentage: item.percentage,
    itemType: item.type,
    color: item.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length],
    isHidden: hiddenCategories.has(item.category),
    budget: item.budget,
    budgetPeriod: item.budgetPeriod
  }));

  const visibleChartData = allChartData.filter(item => !item.isHidden);
  const visibleTotal = visibleChartData.reduce((sum, item) => sum + item.value, 0);

  const summary = statsData?.summary || {
    totalIncome: 0, totalExpense: 0, netBalance: 0, transactionCount: 0
  };

  // Active tab color
  const activeTypeColor = type === 'income' ? config.incomeColor : config.expenseColor;
  const totalAmount = type === 'income' ? summary.totalIncome : summary.totalExpense;
  const highestCategory = chartData.length > 0 ? chartData[0].total : 0;

  // Period label
  const periodLabel = PERIOD_OPTIONS.find(p => p.value === period)?.label || 'This Month';

  // Has active filters?
  const hasActiveFilters = bankName || period !== 'thisMonth';

  if (isLoading && !statsData) {
    return <StatsPageSkeleton />;
  }

  return (
    <div className="space-y-4 pb-4">
      {/* =================== MOBILE =================== */}

      {/* Mobile Header */}
      <StickyPageHeader className="xl:hidden flex items-center justify-between mb-1">
        <h1 className="text-lg font-bold text-surface-900">Stats</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={openFilterModal}
            className={`relative w-9 h-9 rounded-xl flex items-center justify-center border transition-all ${
              hasActiveFilters
                ? 'bg-primary-50 border-primary-200 text-primary-600'
                : 'bg-white border-surface-200 text-surface-600'
            }`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary-500 text-white text-[9px] font-bold flex items-center justify-center">
                {[bankName, period !== 'thisMonth' ? period : null].filter(Boolean).length}
              </span>
            )}
          </button>
        </div>
      </StickyPageHeader>

      {/* Mobile Segmented Control — same style as transaction form */}
      <div className="xl:hidden grid grid-cols-2 gap-1 p-1 bg-surface-100 rounded-xl mb-1">
        {['expense', 'income'].map((t) => {
          const isActive = type === t;
          const tabColor = t === 'income' ? config.incomeColor : config.expenseColor;
          return (
            <button
              key={t}
              onClick={() => updateFilter('type', t)}
              className={`py-2 rounded-lg font-medium text-sm transition-all ${
                isActive ? 'bg-white shadow-sm' : 'text-surface-500'
              }`}
              style={isActive ? { color: tabColor } : {}}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          );
        })}
      </div>

      {/* Mobile Filter Bottom Sheet */}
      {showFilterModal && createPortal(
        <div className="xl:hidden fixed inset-0 z-50 flex items-end !mt-0" onClick={closeFilterModal}>
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/40" />

          {/* Bottom Sheet */}
          <div
            className="relative w-full bg-white rounded-t-2xl animate-slide-up max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-surface-300" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 pb-3">
              <h2 className="text-[15px] font-bold text-surface-900">Filter Stats</h2>
              <div className="flex items-center gap-2">
                {(tempBankName || tempPeriod !== 'thisMonth' || tempStartDate || tempEndDate) && (
                  <button
                    onClick={() => {
                      setTempPeriod('thisMonth');
                      setTempBankName('');
                      setTempStartDate('');
                      setTempEndDate('');
                    }}
                    className="text-xs font-semibold px-2 py-1"
                    style={{ color: 'var(--accent-bg)' }}
                  >
                    Clear All
                  </button>
                )}
                <button
                  onClick={closeFilterModal}
                  className="w-8 h-8 rounded-lg bg-surface-100 flex items-center justify-center text-surface-500"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto px-4 pb-6 space-y-4">
              {/* Time Period */}
              <div>
                <label className="block text-[11px] font-semibold text-surface-400 uppercase tracking-wider mb-2">Time Period</label>
                <div className="grid grid-cols-2 gap-2">
                  {PERIOD_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    const isSelected = tempPeriod === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => setTempPeriod(option.value)}
                        className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-surface-200 bg-white hover:border-surface-300'
                        }`}
                      >
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${option.iconColor}15` }}
                        >
                          <Icon className="w-3.5 h-3.5" style={{ color: option.iconColor }} />
                        </div>
                        <span className={`text-sm font-medium ${isSelected ? 'text-primary-700' : 'text-surface-600'}`}>
                          {option.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom Date Range */}
              {tempPeriod === 'custom' && (
                <div>
                  <div className="flex gap-2 items-center">
                    <div className="flex-1">
                      <DateTimeInput
                        dateOnly
                        value={tempStartDate}
                        onChange={(date) => {
                          setTempStartDate(date);
                          if (tempEndDate && date > tempEndDate) setTempEndDate('');
                        }}
                        placeholder="From"
                      />
                    </div>
                    <span className="text-surface-300 text-sm">&rarr;</span>
                    <div className="flex-1">
                      <DateTimeInput
                        dateOnly
                        value={tempEndDate}
                        onChange={(date) => setTempEndDate(date)}
                        placeholder="To"
                        minDate={tempStartDate || undefined}
                        maxDate={formatLocalDate(new Date())}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Account */}
              <div>
                <label className="block text-[11px] font-semibold text-surface-400 uppercase tracking-wider mb-2">Account</label>
                <MobileSelect
                  value={tempBankName}
                  onChange={(value) => setTempBankName(value)}
                  options={accountOptions}
                  placeholder="All Accounts"
                  label="Select Account"
                  columns={2}
                />
              </div>

              {/* Apply Button */}
              <button
                onClick={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.set('period', tempPeriod);
                  if (tempBankName) newParams.set('bank', tempBankName);
                  else newParams.delete('bank');
                  if (tempPeriod === 'custom' && tempStartDate) newParams.set('startDate', tempStartDate);
                  else newParams.delete('startDate');
                  if (tempPeriod === 'custom' && tempEndDate) newParams.set('endDate', tempEndDate);
                  else newParams.delete('endDate');
                  // Close modal silently (no history.back), replace the modal's history entry with new params
                  closeSilentFilter();
                  setSearchParams(newParams, { replace: true });
                }}
                className="w-full py-3.5 rounded-xl font-semibold text-white transition-all shadow-lg"
                style={{ backgroundColor: 'var(--accent-bg)' }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Mobile Content */}
      <div className="xl:hidden space-y-3">
        {chartData.length > 0 ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { amount: formatAmount(totalAmount), label: type === 'income' ? 'Earned' : 'Spent', color: activeTypeColor },
                { amount: String(summary.transactionCount), label: 'Transactions', color: undefined },
                { amount: formatAmount(highestCategory), label: 'Highest', color: undefined }
              ].map((card, i) => (
                <div key={i} className="bg-white rounded-xl border border-surface-100 px-2 py-3 text-center">
                  <p
                    className="font-bold text-surface-900 leading-tight"
                    style={{
                      color: card.color,
                      fontSize: card.amount.length > 14 ? '9px' : card.amount.length > 11 ? '10px' : card.amount.length > 8 ? '12px' : '14px'
                    }}
                  >
                    {card.amount}
                  </p>
                  <p className="text-[10px] text-surface-400 mt-0.5">{card.label}</p>
                </div>
              ))}
            </div>

            {/* Donut Chart */}
            <div className="bg-white rounded-2xl border border-surface-100 p-4">
              <div className="relative h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={visibleChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={2}
                      dataKey="value"
                      stroke="none"
                    >
                      {visibleChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload;
                        return (
                          <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-surface-200 text-xs">
                            <p className="font-semibold text-surface-900">{d.name}</p>
                            <p style={{ color: d.color }}>{formatAmount(d.value)} ({d.percentage}%)</p>
                          </div>
                        );
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                {/* Center text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className={`font-extrabold ${formatAmount(visibleTotal).length > 14 ? 'text-sm' : formatAmount(visibleTotal).length > 11 ? 'text-base' : 'text-xl'}`} style={{ color: activeTypeColor }}>{formatAmount(visibleTotal)}</span>
                  <span className="text-[10px] text-surface-400">{periodLabel}</span>
                </div>
              </div>
            </div>

            {/* Category List */}
            <div>
              <p className="text-[11px] font-semibold text-surface-400 uppercase tracking-wider mb-2 px-1">Categories</p>
              <div className="space-y-2">
                {visibleChartData.map((item) => {
                  const catInfo = categoryIconMap[`${item.itemType}-${item.name}`];
                  const CatIcon = catInfo?.icon || (item.itemType === 'income' ? TrendingUp : TrendingDown);
                  const hasBudget = item.itemType === 'expense' && item.budget && item.budget > 0;
                  const budgetPct = hasBudget ? Math.round((item.value / item.budget) * 100) : 0;
                  const isOverBudget = budgetPct > 100;
                  const isNearBudget = budgetPct >= 80 && budgetPct <= 100;

                  const progressColor = hasBudget
                    ? (isOverBudget ? '#ef4444' : isNearBudget ? '#f59e0b' : '#10b981')
                    : item.color;

                  const budgetPeriodLabel = { monthly: '/mo', quarterly: '/qtr', yearly: '/yr' }[item.budgetPeriod] || '/mo';

                  return (
                    <button
                      key={item.name}
                      onClick={() => navigateToCategory(item)}
                      className="w-full bg-white rounded-xl border border-surface-100 p-3.5 flex items-center gap-3 text-left active:bg-surface-50 transition-colors"
                    >
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${item.color}12` }}
                      >
                        <CatIcon className="w-[18px] h-[18px]" style={{ color: item.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className={`font-semibold text-surface-900 ${item.name.length > 15 ? 'text-xs' : 'text-sm'}`}>{item.name}</span>
                            {isOverBudget && (
                              <span className="text-[9px] font-bold text-red-500 bg-red-50 px-1.5 py-0.5 rounded flex-shrink-0">OVER</span>
                            )}
                          </div>
                          <div className="flex-shrink-0 ml-2">
                            {hasBudget ? (
                              <span className="text-sm font-bold" style={{ color: progressColor }}>
                                {formatAmount(item.value)}
                                <span className="text-surface-300"> / </span>
                                <span className="text-surface-400 font-medium text-xs">{formatAmount(item.budget)}{budgetPeriodLabel}</span>
                              </span>
                            ) : (
                              <span className="text-sm font-bold" style={{ color: activeTypeColor }}>
                                {formatAmount(item.value)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-[11px] text-surface-400 mb-1.5">
                          <span>{item.count} transaction{item.count !== 1 ? 's' : ''}</span>
                          <span style={hasBudget ? { color: progressColor, fontWeight: 600 } : {}}>
                            {hasBudget ? `${budgetPct}%` : `${item.percentage}%`}
                          </span>
                        </div>
                        <div className="h-1 bg-surface-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: hasBudget ? `${Math.min(budgetPct, 100)}%` : `${item.percentage}%`,
                              backgroundColor: progressColor
                            }}
                          />
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-surface-300 flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <div className="bg-white rounded-2xl border border-surface-100 p-10 text-center">
            <div className="w-14 h-14 bg-surface-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Wallet className="w-7 h-7 text-surface-400" />
            </div>
            <p className="text-surface-900 font-medium">No data available</p>
            <p className="text-surface-400 text-sm mt-1">Try adjusting your filters</p>
          </div>
        )}
      </div>

      {/* =================== DESKTOP =================== */}

      {/* Desktop Header */}
      <div className="hidden xl:flex xl:items-center xl:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Stats</h1>
          <p className="page-subtitle">Category-wise insights</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/transactions')}
            className="flex items-center justify-center w-10 h-10 rounded-xl border bg-white border-surface-200 text-surface-600 hover:border-surface-300 transition-all"
            title="Transactions"
          >
            <Book className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Desktop Filters */}
      <div className="hidden xl:block card p-4 space-y-4">
        <div className="grid grid-cols-2 gap-1 p-1 bg-surface-100 rounded-xl">
          {['expense', 'income'].map((t) => (
            <button
              key={t}
              onClick={() => updateFilter('type', t)}
              className={`py-2.5 rounded-lg font-medium text-sm transition-all ${
                type === t ? 'bg-white shadow-sm' : 'text-surface-500'
              }`}
              style={type === t ? { color: t === 'income' ? config.incomeColor : config.expenseColor } : {}}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="relative">
            <select
              value={period}
              onChange={(e) => updateFilter('period', e.target.value)}
              className="w-full px-4 py-2.5 bg-surface-50 border border-surface-200 rounded-xl text-surface-900 text-sm appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400 pointer-events-none" />
          </div>
          <div className="relative">
            <select
              value={bankName}
              onChange={(e) => updateFilter('bank', e.target.value)}
              className="w-full px-4 py-2.5 bg-surface-50 border border-surface-200 rounded-xl text-surface-900 text-sm appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Accounts</option>
              {accounts.filter(a => a.isActive !== false).map((acc) => (
                <option key={acc._id} value={acc.name}>{acc.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400 pointer-events-none" />
          </div>
        </div>
        {period === 'custom' && (
          <div className="grid grid-cols-2 gap-3">
            <DateTimeInput
              dateOnly
              value={localStartDate}
              onChange={(date) => {
                setLocalStartDate(date);
                if (localEndDate && date > localEndDate) {
                  setLocalEndDate('');
                  updateFilter('startDate', date, { endDate: '' });
                } else {
                  updateFilter('startDate', date);
                }
              }}
              placeholder="From"
            />
            <DateTimeInput
              dateOnly
              value={localEndDate}
              onChange={(date) => {
                setLocalEndDate(date);
                updateFilter('endDate', date);
              }}
              placeholder="To"
              minDate={localStartDate || undefined}
            />
          </div>
        )}
      </div>

      {/* Desktop Chart + Categories */}
      <div className="hidden xl:block space-y-4">
        {chartData.length > 0 ? (
          <>
            <div className="card p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-surface-900 text-base">Category Breakdown</h3>
                <span className="text-xs text-surface-500 bg-surface-100 px-2 py-1 rounded-lg">
                  {summary.transactionCount} transactions
                </span>
              </div>
              <div className="relative h-56 [&_svg]:outline-none [&_.recharts-surface]:outline-none [&_.recharts-wrapper]:outline-none [&_*]:outline-none">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={visibleChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      stroke="none"
                    >
                      {visibleChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload;
                        return (
                          <div className="bg-white p-3 rounded-lg shadow-lg border border-surface-200">
                            <p className="font-medium text-surface-900">{d.name}</p>
                            <p style={{ color: d.itemType === 'income' ? config.incomeColor : config.expenseColor }}>
                              {formatAmount(d.value)}
                            </p>
                            <p className="text-surface-500 text-sm">{d.count} transactions</p>
                            <p className="text-surface-400 text-sm">{d.percentage}%</p>
                          </div>
                        );
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-xl font-extrabold text-surface-900">{formatAmount(visibleTotal)}</span>
                  <span className="text-xs text-surface-400">{periodLabel}</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 justify-center mt-3">
                {allChartData.map((item, index) => {
                  const pct = visibleTotal > 0 && !item.isHidden ? ((item.value / visibleTotal) * 100).toFixed(1) : 0;
                  return (
                    <button
                      key={index}
                      onClick={() => toggleCategory(item.name)}
                      className={`flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-surface-100 transition-all ${item.isHidden ? 'opacity-40' : ''}`}
                    >
                      <div
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: item.isHidden ? '#9ca3af' : item.color }}
                      />
                      <span className={`truncate max-w-[80px] text-[11px] font-medium ${item.isHidden ? 'line-through text-surface-400' : 'text-surface-700'}`}>
                        {item.name}
                      </span>
                      {!item.isHidden && <span className="text-[10px] text-surface-400 font-medium">{pct}%</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="card overflow-hidden">
              <div className="p-4 border-b border-surface-100">
                <h3 className="font-semibold text-surface-900">Categories</h3>
              </div>
              <div className="divide-y divide-surface-100">
                {visibleChartData.map((item) => {
                  const hasBudget = item.itemType === 'expense' && item.budget && item.budget > 0;
                  const budgetPercentage = hasBudget ? Math.round((item.value / item.budget) * 100) : 0;
                  const isOverBudget = budgetPercentage > 100;
                  const isNearBudget = budgetPercentage >= 80 && budgetPercentage <= 100;
                  const getProgressBarColor = () => {
                    if (!hasBudget) return item.color;
                    if (isOverBudget) return '#ef4444';
                    if (isNearBudget) return '#f59e0b';
                    return '#10b981';
                  };
                  const budgetPeriodLabel = { monthly: '/mo', quarterly: '/qtr', yearly: '/yr' }[item.budgetPeriod] || '/mo';

                  return (
                    <button
                      key={item.name}
                      onClick={() => navigateToCategory(item)}
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-surface-50 transition-colors"
                    >
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-medium text-surface-900 truncate">{item.name}</span>
                            {isOverBudget && <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                          </div>
                          <div className="text-right flex-shrink-0 ml-2">
                            {hasBudget ? (
                              <span className="font-semibold" style={{ color: getProgressBarColor() }}>
                                {formatAmount(item.value)} <span className="text-surface-400">/</span> {formatAmount(item.budget)}{budgetPeriodLabel}
                              </span>
                            ) : (
                              <span className="font-semibold" style={{ color: item.itemType === 'income' ? config.incomeColor : config.expenseColor }}>
                                {formatAmount(item.value)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-xs text-surface-500">
                          <span>{item.count} transactions</span>
                          <span>{hasBudget ? <span style={{ color: getProgressBarColor() }}>{budgetPercentage}% of budget</span> : `${item.percentage}%`}</span>
                        </div>
                        <div className="mt-2 h-1.5 bg-surface-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: hasBudget ? `${Math.min(budgetPercentage, 100)}%` : `${item.percentage}%`,
                              backgroundColor: getProgressBarColor()
                            }}
                          />
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-surface-300 flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <div className="card p-8 text-center">
            <div className="w-16 h-16 bg-surface-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Wallet className="w-8 h-8 text-surface-400" />
            </div>
            <p className="text-surface-900 font-medium">No data available</p>
            <p className="text-surface-500 text-sm mt-1">Try adjusting your filters</p>
          </div>
        )}
      </div>
    </div>
  );
}
